import { useCallback, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * SSE client for `/api/help/ask` — the "Ask Fleetwise" help agent.
 *
 * Deliberately mirrors `features/roldi/useRoldiStream.ts`: same auth (a bearer
 * token read from the live Supabase session), same POST-and-read-the-body
 * approach (EventSource cannot send an Authorization header), same `\n\n` frame
 * split. Two hooks, one transport contract — if the SSE shape ever changes,
 * both fail the same way instead of one silently drifting.
 *
 * What it does NOT share: tool activity, a conversation id, or any persisted
 * state. A help turn is ephemeral by design — the server writes no
 * `conversations` / `messages` rows, so the only memory of this thread is the
 * caller's React state.
 */

/** A manual section the answer was grounded on. `link` is always in-app. */
export interface HelpSource {
  id: string;
  title: string;
  chapter: string;
  link: string;
}

/** One prior turn, replayed to the server so follow-up questions work. */
export interface HelpHistoryTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface HelpStreamRequest {
  question: string;
  /** The route the asker is on, so "what is this page" is answerable. */
  currentPath?: string;
  /** Prior turns in THIS session. Must alternate and start on a user turn. */
  history?: HelpHistoryTurn[];
}

export interface HelpSendResult {
  text: string;
  sources: HelpSource[];
  /** Human-readable failure copy, or null when the turn completed. */
  error: string | null;
}

export interface HelpStreamState {
  streaming: boolean;
  /** Accumulated answer text for the turn in flight. */
  text: string;
  sources: HelpSource[];
  error: string | null;
}

interface HelpServerEvent {
  type?: string;
  text?: string;
  sources?: unknown;
  message?: string;
}

const EMPTY_STATE: HelpStreamState = {
  streaming: false,
  text: '',
  sources: [],
  error: null,
};

/**
 * Failure copy. A person on the shop floor never sees a status code or a stack
 * — they see what happened and whether trying again is worth it.
 */
const TROUBLE_MESSAGE =
  'Help is having trouble right now. Try again in a moment — everything else keeps working.';
const OFFLINE_MESSAGE = "Couldn't reach help just now. Check the connection and try again.";
const SIGNED_OUT_MESSAGE = 'Your session has expired. Sign out and back in, then ask again.';

/**
 * Boundary validation on the `open` event. The payload is ours, but a source
 * list drives navigation, so anything that isn't a plain in-app path is
 * dropped rather than rendered — `//host` is protocol-relative and would leave
 * the app entirely.
 */
function normalizeSources(raw: unknown): HelpSource[] {
  if (!Array.isArray(raw)) return [];
  const out: HelpSource[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const rec = item as Record<string, unknown>;
    const { id, title, chapter, link } = rec;
    if (
      typeof id !== 'string' ||
      typeof title !== 'string' ||
      typeof chapter !== 'string' ||
      typeof link !== 'string'
    ) {
      continue;
    }
    if (!link.startsWith('/') || link.startsWith('//')) continue;
    out.push({ id, title, chapter, link });
  }
  return out;
}

/**
 * Turn a non-200 into copy a technician can act on. Only the rate-limit path
 * forwards the server's own wording — those messages ("give it a moment",
 * "resets at midnight UTC") are written for humans and say something the
 * generic line can't.
 */
async function friendlyHttpError(response: Response): Promise<string> {
  if (response.status === 401) return SIGNED_OUT_MESSAGE;

  let payload: { message?: unknown } | null = null;
  try {
    payload = (await response.json()) as { message?: unknown };
  } catch {
    payload = null;
  }

  if (response.status === 429 && typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }
  return TROUBLE_MESSAGE;
}

export function useHelpStream() {
  const [state, setState] = useState<HelpStreamState>(EMPTY_STATE);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState(EMPTY_STATE);
  }, []);

  const send = useCallback(async (req: HelpStreamRequest): Promise<HelpSendResult> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState({ ...EMPTY_STATE, streaming: true });

    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) {
      setState({ ...EMPTY_STATE, error: SIGNED_OUT_MESSAGE });
      return { text: '', sources: [], error: SIGNED_OUT_MESSAGE };
    }

    let assembled = '';
    let sources: HelpSource[] = [];
    let failure: string | null = null;
    let sawDone = false;

    try {
      const response = await fetch('/api/help/ask', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          question: req.question,
          currentPath: req.currentPath,
          history: req.history && req.history.length > 0 ? req.history : undefined,
        }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        const message = await friendlyHttpError(response);
        setState({ ...EMPTY_STATE, error: message });
        return { text: '', sources: [], error: message };
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx = buffer.indexOf('\n\n');
        while (idx !== -1) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          idx = buffer.indexOf('\n\n');

          const dataLine = raw.split('\n').find((l) => l.startsWith('data:'));
          if (!dataLine) continue;

          const payload = dataLine.slice(5).trim();
          if (!payload) continue;

          let event: HelpServerEvent;
          try {
            event = JSON.parse(payload) as HelpServerEvent;
          } catch {
            continue;
          }

          if (event.type === 'open') {
            sources = normalizeSources(event.sources);
            setState((s) => ({ ...s, sources }));
          } else if (event.type === 'delta' && typeof event.text === 'string') {
            assembled += event.text;
            setState((s) => ({ ...s, text: assembled }));
          } else if (event.type === 'done') {
            sawDone = true;
            setState((s) => ({ ...s, streaming: false }));
          } else if (event.type === 'error') {
            failure =
              typeof event.message === 'string' && event.message.trim()
                ? event.message
                : TROUBLE_MESSAGE;
            setState((s) => ({ ...s, streaming: false, error: failure }));
          }
        }
      }

      // A stream that ends without a `done` frame was cut off mid-flight
      // (proxy timeout, dropped connection). Say so rather than leaving a
      // half-answer sitting there looking finished.
      if (!sawDone && !failure && assembled.trim().length === 0) {
        failure = TROUBLE_MESSAGE;
        setState((s) => ({ ...s, error: failure }));
      }
      setState((s) => ({ ...s, streaming: false }));
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        setState((s) => ({ ...s, streaming: false }));
        return { text: assembled, sources, error: null };
      }
      failure = OFFLINE_MESSAGE;
      setState((s) => ({ ...s, streaming: false, error: OFFLINE_MESSAGE }));
    } finally {
      abortRef.current = null;
    }

    return { text: assembled, sources, error: failure };
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { ...state, send, cancel, reset };
}
